import React, { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Divider,
  TextField,
  Checkbox,
  Select,
} from '@shopify/polaris';

const ORDER_OPTIONS = [
  {
    id: 'instant_print',
    icon: '🖨️',
    title: 'Instant Print',
    description: 'Create order and send directly to printing queue',
  },
  {
    id: 'home_delivery',
    icon: '🚚',
    title: 'Home Delivery',
    description: 'Create order for shipping to customer address',
  },
  {
    id: 'order_later',
    icon: '📋',
    title: 'Order Later',
    description: 'Save this preview to process later',
  },
];

const PAYMENT_OPTIONS = [
  { label: 'Cash', value: 'cash' },
  { label: 'Card', value: 'card' },
  { label: 'InstaPay', value: 'instapay' },
];

const BASE_PRICE = 1100;
const ADDON_GIFT_BOX = { id: 'gift_box', label: 'Gift Box', price: 500 };
const ADDON_GIFT_NOTE = { id: 'gift_note', label: 'Gift Note', price: 200 };

const ConfirmOrderModal = ({
  open,
  onClose,
  preview,
  onConfirm,
  isSubmitting,
}) => {
  // Step: 'select_type' or 'collect_info'
  const [step, setStep] = useState('select_type');
  const [selectedOption, setSelectedOption] = useState(null);

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [baseAmount, setBaseAmount] = useState(String(BASE_PRICE));
  const [giftBox, setGiftBox] = useState(false);
  const [giftNote, setGiftNote] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [giftNoteText, setGiftNoteText] = useState('');

  // Validation
  const [errors, setErrors] = useState({});

  const totalAmount = useMemo(() => {
    let total = parseInt(baseAmount, 10) || 0;
    if (giftBox) total += ADDON_GIFT_BOX.price;
    if (giftNote) total += ADDON_GIFT_NOTE.price;
    return total;
  }, [baseAmount, giftBox, giftNote]);

  const isHomeDelivery = selectedOption === 'home_delivery';
  const isOrderLater = selectedOption === 'order_later';

  const resetForm = useCallback(() => {
    setStep('select_type');
    setSelectedOption(null);
    setCustomerName('');
    setCustomerPhone('');
    setPaymentMethod('cash');
    setBaseAmount(String(BASE_PRICE));
    setGiftBox(false);
    setGiftNote(false);
    setDeliveryAddress('');
    setGiftNoteText('');
    setErrors({});
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleNext = useCallback(() => {
    if (selectedOption) {
      setStep('collect_info');
    }
  }, [selectedOption]);

  const handleBack = useCallback(() => {
    setStep('select_type');
    setErrors({});
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!customerName.trim()) newErrors.customerName = 'Customer name is required';
    if (!customerPhone.trim()) newErrors.customerPhone = 'Phone number is required';
    if (!isOrderLater) {
      if (!paymentMethod) newErrors.paymentMethod = 'Payment method is required';
      if (!baseAmount || parseInt(baseAmount, 10) <= 0) newErrors.baseAmount = 'Valid amount is required';
      if (isHomeDelivery && !deliveryAddress.trim()) newErrors.deliveryAddress = 'Delivery address is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [customerName, customerPhone, paymentMethod, baseAmount, isHomeDelivery, isOrderLater, deliveryAddress]);

  const handleConfirm = useCallback(() => {
    if (!validate()) return;
    if (preview && selectedOption) {
      if (isOrderLater) {
        onConfirm(preview, selectedOption, {
          customer_name: customerName.trim(),
          customer_phone: customerPhone.trim(),
        });
        return;
      }

      const addons = [];
      if (giftBox) addons.push({ type: 'gift_box', label: ADDON_GIFT_BOX.label, price: ADDON_GIFT_BOX.price });
      if (giftNote) addons.push({ type: 'gift_note', label: ADDON_GIFT_NOTE.label, price: ADDON_GIFT_NOTE.price, message: giftNoteText.trim() });

      onConfirm(preview, selectedOption, {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        payment_method: paymentMethod,
        base_amount: parseInt(baseAmount, 10),
        total_amount: totalAmount,
        addons,
        delivery_address: isHomeDelivery ? deliveryAddress.trim() : undefined,
      });
    }
  }, [validate, preview, selectedOption, customerName, customerPhone, paymentMethod, baseAmount, totalAmount, giftBox, giftNote, giftNoteText, isHomeDelivery, isOrderLater, deliveryAddress, onConfirm]);

  if (!preview) return null;

  const coverScene = preview.generated_scenes?.find(s => s.sceneType === 'cover');
  const coverUrl = coverScene?.processedImageUrl;
  const selectedOptionData = ORDER_OPTIONS.find(o => o.id === selectedOption);

  // ── Step 1: Select Order Type ────────────────────────────────
  const renderStepSelectType = () => (
    <BlockStack gap="400">
      {/* Preview summary */}
      <div style={{
        display: 'flex', gap: '16px', padding: '12px',
        background: '#f6f6f7', borderRadius: '12px',
      }}>
        {coverUrl && (
          <img src={coverUrl} alt="Cover" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' }} />
        )}
        <BlockStack gap="100">
          <Text variant="headingSm" as="h3">{preview.kid_name}</Text>
          <Text variant="bodySm" tone="subdued">Story: {preview.story_name || preview.story_slug}</Text>
          <Text variant="bodySm" tone="subdued">Preview ID: {preview.preview_id}</Text>
        </BlockStack>
      </div>

      <Divider />
      <Text variant="headingSm" as="h3">Select Order Type</Text>

      <BlockStack gap="300">
        {ORDER_OPTIONS.map((option) => {
          const isSelected = selectedOption === option.id;
          return (
            <div
              key={option.id}
              onClick={() => setSelectedOption(option.id)}
              style={{
                padding: '16px', borderRadius: '12px',
                border: isSelected ? '2px solid var(--p-color-border-interactive)' : '2px solid #e3e3e3',
                background: isSelected ? '#f0f5ff' : '#fff',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = '#999'; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = isSelected ? 'var(--p-color-border-interactive)' : '#e3e3e3'; }}
            >
              <InlineStack gap="300" align="start" blockAlign="center">
                <span style={{ fontSize: 28 }}>{option.icon}</span>
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="bodyMd" fontWeight="bold">{option.title}</Text>
                    {isSelected && <Badge tone="info">Selected</Badge>}
                  </InlineStack>
                  <Text variant="bodySm" tone="subdued">{option.description}</Text>
                </BlockStack>
              </InlineStack>
            </div>
          );
        })}
      </BlockStack>
    </BlockStack>
  );

  // ── Step 2: Collect Customer Info ────────────────────────────
  const renderStepCollectInfo = () => (
    <BlockStack gap="400">
      {/* Selected order type badge */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 14px', background: '#f0f5ff', borderRadius: '10px',
      }}>
        <span style={{ fontSize: 22 }}>{selectedOptionData?.icon}</span>
        <Text variant="bodyMd" fontWeight="semibold">{selectedOptionData?.title}</Text>
        <Button variant="plain" onClick={handleBack}>Change</Button>
      </div>

      <Divider />

      {/* Customer Info */}
      <Text variant="headingSm" as="h3">Customer Information</Text>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <TextField
          label="Customer Name"
          value={customerName}
          onChange={setCustomerName}
          placeholder="Enter customer name"
          autoComplete="off"
          error={errors.customerName}
        />
        <TextField
          label="Phone Number"
          value={customerPhone}
          onChange={setCustomerPhone}
          placeholder="e.g. 01012345678"
          autoComplete="off"
          error={errors.customerPhone}
        />
      </div>

      {/* Payment, Pricing, Addons — hidden for Order Later */}
      {!isOrderLater && (
        <>
          <Select
            label="Payment Method"
            options={PAYMENT_OPTIONS}
            value={paymentMethod}
            onChange={setPaymentMethod}
            error={errors.paymentMethod}
          />

          <Divider />

          {/* Pricing */}
          <Text variant="headingSm" as="h3">Pricing</Text>

          <TextField
            label="Base Amount (EGP)"
            type="number"
            value={baseAmount}
            onChange={setBaseAmount}
            autoComplete="off"
            error={errors.baseAmount}
            suffix="EGP"
          />

          {/* Addons */}
          <BlockStack gap="200">
            <Text variant="bodySm" fontWeight="semibold">Addons</Text>
            <Checkbox
              label={`Gift Box (+${ADDON_GIFT_BOX.price} EGP)`}
              checked={giftBox}
              onChange={setGiftBox}
            />
            <Checkbox
              label={`Gift Note (+${ADDON_GIFT_NOTE.price} EGP)`}
              checked={giftNote}
              onChange={setGiftNote}
            />
            {giftNote && (
              <div style={{ paddingLeft: '24px' }}>
                <TextField
                  label="Gift Note Message"
                  value={giftNoteText}
                  onChange={setGiftNoteText}
                  placeholder="Write a personal message..."
                  multiline={2}
                  autoComplete="off"
                />
              </div>
            )}
          </BlockStack>

          {/* Total */}
          <div style={{
            padding: '12px 16px', background: '#f1f8f1', borderRadius: '10px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <Text variant="bodyMd" fontWeight="semibold">Total Amount</Text>
            <Text variant="headingMd" fontWeight="bold" tone="success">
              {totalAmount.toLocaleString()} EGP
            </Text>
          </div>

          {/* Delivery Address (Home Delivery only) */}
          {isHomeDelivery && (
            <>
              <Divider />
              <Text variant="headingSm" as="h3">Delivery Address</Text>
              <TextField
                label="Full Address"
                value={deliveryAddress}
                onChange={setDeliveryAddress}
                placeholder="Enter delivery address"
                multiline={3}
                autoComplete="off"
                error={errors.deliveryAddress}
              />
            </>
          )}
        </>
      )}
    </BlockStack>
  );

  // ── Modal Actions ─────────────────────────────────────────────
  const primaryAction = step === 'select_type'
    ? {
        content: 'Next →',
        onAction: handleNext,
        disabled: !selectedOption,
      }
    : {
        content: isOrderLater ? 'Save for Later' : `Confirm Order · ${totalAmount.toLocaleString()} EGP`,
        onAction: handleConfirm,
        disabled: isSubmitting,
        loading: isSubmitting,
      };

  const secondaryActions = step === 'select_type'
    ? [{ content: 'Cancel', onAction: handleClose }]
    : [{ content: '← Back', onAction: handleBack }];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={step === 'select_type' ? 'Confirm Order' : 'Order Details'}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      large={step === 'collect_info'}
    >
      <Modal.Section>
        {step === 'select_type' ? renderStepSelectType() : renderStepCollectInfo()}
      </Modal.Section>
    </Modal>
  );
};

export default ConfirmOrderModal;
